import { Button, Typography } from "@mui/material";
import React, { ChangeEvent, useRef, useState } from "react";
import "./dataUpload.css";
import { usePortalTranslation } from "../../../../../../providers/TranslationProvider";
import { openPopup } from "../../../../../../stateManagement/actions/popupActions";
import { mdmDataUpload } from "../../../../../../services/mdmService";
import uploadIcon from "../../../../../../assets/image/svg/uploadIcon.svg";
import { DomainValue } from "../../../../../../services/mdmService/types";
import { Loader } from "../../../../../loader/Loader";
import excelIcon from "../../../../../../images/XLS.png";
import pdfIcon from "../../../../../../images/PDF.png";

const excelFileTypes =
  ".csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, text/csv";
const zipFileTypes = ".zip, .rar";

interface MDMfileUploadProps {
  Open: boolean;
  setShowModal: React.Dispatch<React.SetStateAction<boolean>>;
  downloadUrl: string | undefined;
  selectedMaster: DomainValue;
  loader: boolean;
  setLoader: React.Dispatch<React.SetStateAction<boolean>>;
  setSelectedMaster: React.Dispatch<
    React.SetStateAction<DomainValue | undefined>
  >;
  setFile: React.Dispatch<React.SetStateAction<File | undefined>>; // New prop
  file: File | undefined; // New prop
}

export const MDMfileUpload: React.FC<MDMfileUploadProps> = ({
  Open,
  setShowModal,
  downloadUrl,
  selectedMaster,
  loader,
  setLoader,
  setSelectedMaster,
  setFile, // new prop for file state setter
  file, // new prop for file state
}) => {
  // Ref and state management
  const [blobData, setBlobData] = useState(new FormData());
  const [uploadColor, setUploadColor] = useState<string>("grey");
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [fileName, setFileName] = useState<string>("");
  const [imageDisplayIcon, setImageDisplayIcon] = useState<string>("");

  // Set accepted file types based on selected master
  const acceptAttribute =
    selectedMaster.fileType === "zip" ? zipFileTypes : excelFileTypes;

  // Handle clicking on the upload button
  const handleClick = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Handle drag & drop events
  const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(true);
  };

  const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setIsDragging(false);
    const droppedFile = event.dataTransfer.files?.[0];
    if (droppedFile) {
      handleFileUpload(droppedFile);
    }
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  // Handle file selection from input or drag/drop
  const handleFileUpload = (selectedFile: File | undefined) => {
    if (file) {
      setFile(file);
      setImageDisplayIcon(
        acceptAttribute === excelFileTypes ? excelIcon : pdfIcon
      );
      setFileName(file.name);
      const data = new FormData();
      data.append("file", file);
      setBlobData(data);
      setUploadColor("#24c6b1");
    }
    if (selectedFile) {
      setFile(selectedFile);
      setImageDisplayIcon(
        acceptAttribute === excelFileTypes ? excelIcon : pdfIcon
      );
      setFileName(selectedFile.name);
    }
  };

  // Handle file input change
  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    const fileToBeUploaded = event.target.files?.[0];
    handleFileUpload(fileToBeUploaded);
  };

  // Handle file upload process
  const uploadOnClick = async (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ) => {
    e.stopPropagation();
    if (uploadColor === "grey") {
      openPopup("Error", "Please select a file");
      return;
    }
    setLoader(true);
    setShowModal(false);
    await mdmDataUpload(
      blobData,
      selectedMaster.entity ?? "",
      file,
      setLoader,
      selectedMaster.useKafka ?? false,
      selectedMaster.transformerInfo
    );
    setFileName("");
    setImageDisplayIcon("")
    setSelectedMaster(selectedMaster);
    setUploadColor("grey");
  };

  return (
    <>
      <div
        className={`mdm-file-upload-container ${isDragging ? "dragging" : ""}`}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
        onDragLeave={handleDragLeave}
      >
        <div className="mdm-file-upload" onClick={handleClick}>
          <Typography variant="subtitle1" fontSize={"14px"}>
            Upload user template filled with your data
          </Typography>
          <Typography variant="subtitle1" fontSize={"11px"}>
            Drag & drop or Browse file to upload
          </Typography>

          {/* Hidden file input */}
          <input
            type="file"
            ref={fileInputRef}
            hidden
            accept={acceptAttribute}
            onChange={handleInputChange}
          />

          {/* Upload Button */}
          {/* <Button
            variant="contained"
            sx={{
              width: "20%",
              backgroundColor: "#00C6B1",
              color: "#fff",
              padding: "5px 12px",
              fontSize: "14px",
              "&:hover": {
                backgroundColor: "#00C6B1",
              },
              display: "flex",
              justifyContent: "space-between",
              marginTop: "15px",
            }}
            onClick={uploadOnClick}
          >
            <>
              <img src={uploadButtonIcon} alt="Upload" />
              Upload
            </>
          </Button> */}
          <Button
            variant="outlined"
            sx={{
              color: "primary",
              borderColor: "#primary",
              marginRight: "10px",
              width: "25%",
              padding: "5px 5px",
              marginTop: "15px",
              borderRadius: "10px",
            }}
          >
            Select File Here
          </Button>
          {/* Display file name */}
          {fileName && (
            <>
              <img
                src={imageDisplayIcon}
                height="40"
                width="auto"
                style={{marginTop:"10px",marginBottom:"5px"}}
                alt="banner preview"
              />
              <Typography
                variant="subtitle2"
                className="file-name"
                sx={{ marginTop: "5px" }}
              >
                {fileName}
              </Typography>
            </>
          )}
        </div>
      </div>
    </>
  );
};

export default MDMfileUpload;
